import { useState } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/authStore';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
} from 'lucide-react';

/**
 * VendorLayout - Layout for the vendor/marketplace portal
 */
export default function VendorLayout() {
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/vendor/dashboard', icon: LayoutDashboard },
    { name: 'Products', href: '/vendor/products', icon: Package },
    { name: 'Orders', href: '/vendor/orders', icon: ShoppingCart },
  ];

  return (
    <div className="min-h-screen bg-cream">
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-navy/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full bg-cream-dark border-r border-warm-gray/10
          transition-all duration-smooth
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-warm-gray/10">
          <Link to="/vendor/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-terracotta to-terracotta-light rounded-xl flex items-center justify-center text-white flex-shrink-0">
              <Package className="w-5 h-5" />
            </div>
            {sidebarOpen && (
              <div>
                <span className="font-display text-lg font-semibold text-navy">BrightPath</span>
                <span className="block text-xs text-warm-gray">Vendor Portal</span>
              </div>
            )}
          </Link>
          
          <button 
            className="lg:hidden p-2 rounded-md hover:bg-cream"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-md transition-fast
                  ${isActive ? 'bg-terracotta text-white' : 'text-navy hover:bg-cream'}
                `}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.name}</span>}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="hidden lg:flex p-3 border-t border-warm-gray/10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-center w-full py-2 px-3 rounded-md text-warm-gray hover:bg-cream transition-fast"
          >
            <Menu className="w-5 h-5" />
            {sidebarOpen && <span className="ml-2 text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      <div className={`transition-all duration-smooth ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <header className="sticky top-0 z-30 h-16 bg-cream/95 backdrop-blur-md border-b border-warm-gray/10">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-cream-dark"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-4">
              <button className="relative p-2 rounded-md hover:bg-cream-dark transition-fast">
                <Bell className="w-5 h-5 text-navy" />
              </button>

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-cream-dark transition-fast"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-terracotta to-terracotta-light rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <ChevronDown className="w-4 h-4 text-warm-gray" />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-medium border border-warm-gray/10 py-1 z-20">
                      <div className="px-4 py-3 border-b border-warm-gray/10">
                        <p className="text-sm font-medium text-navy">{user?.first_name} {user?.last_name}</p>
                        <p className="text-xs text-warm-gray">{user?.email}</p>
                      </div>
                      <button
                        onClick={() => logout()}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-terracotta hover:bg-cream transition-fast"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

